// Enhanced validation utilities with reusable functions
import { isModifierRequired } from './modifierUtils';
import { shouldShowSpiceLevel } from './spiceUtils';

export interface ValidationErrors {
  [key: string]: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationErrors;
}

// Reusable spice level validation
export const validateSpiceLevel = (item: any, spiceLevel: number): string | null => {
  if (shouldShowSpiceLevel(item) && (!spiceLevel || spiceLevel === 0)) {
    return "Please select a spice level";
  }
  return null;
};

// Reusable modifier validation
export const validateModifier = (modifier: any, selectedModifiers: any[]): string | null => {
  if (isModifierRequired(modifier)) {
    const selectedModifier = selectedModifiers.find(mod => mod.name === modifier.name);
    if (!selectedModifier || selectedModifier.options.length === 0) {
      const modifierName = modifier.name.toLowerCase();
      
      // Check if modifier name already contains addon-related words
      const hasAddonWords = /\b(addon|add-on|option|choice)\b/i.test(modifierName);
      
      if (hasAddonWords) {
        return `Please select at least one ${modifierName}`;
      } else {
        return `Please select at least one ${modifierName} option`;
      }
    }
  }
  return null;
};

// Enhanced validation with proper error messages
export const validateAllSelections = (
  item: any,
  modifiers: any[],
  selectedModifiers: any[],
  spiceLevel: number
): ValidationResult => {
  const errors: ValidationErrors = {};
  let isValid = true;

  // Validate spice level using reusable function
  const spiceError = validateSpiceLevel(item, spiceLevel);
  if (spiceError) {
    errors['Spice Level'] = spiceError;
    isValid = false;
  }

  // Validate modifiers using reusable function
  modifiers.forEach(modifier => {
    const modifierError = validateModifier(modifier, selectedModifiers);
    if (modifierError) {
      errors[modifier.name] = modifierError;
      isValid = false;
    }
  });

  return { isValid, errors };
};

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Phone validation regex (basic international format)
const PHONE_REGEX = /^[\+]?[1-9][\d]{0,15}$/;

// Contact form data interface
export interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
}

// Enhanced contact form field validators
export const validateFirstName = (firstName: string): string | null => {
  if (!firstName || firstName.trim().length === 0) {
    return "First name is required";
  }
  if (firstName.trim().length < 2) {
    return "First name must be at least 2 characters";
  }
  if (firstName.trim().length > 50) {
    return "First name must be less than 50 characters";
  }
  // Check for invalid characters (allow letters, spaces, hyphens, apostrophes)
  if (!/^[a-zA-Z\s\-']+$/.test(firstName.trim())) {
    return "First name can only contain letters, spaces, hyphens, and apostrophes";
  }
  return null;
};

export const validateLastName = (lastName: string): string | null => {
  // Last name is optional, but if provided, validate it
  if (!lastName || lastName.trim().length === 0) {
    return null; // Optional field
  }
  if (lastName.trim().length < 2) {
    return "Last name must be at least 2 characters";
  }
  if (lastName.trim().length > 50) {
    return "Last name must be less than 50 characters";
  }
  // Check for invalid characters (allow letters, spaces, hyphens, apostrophes)
  if (!/^[a-zA-Z\s\-']+$/.test(lastName.trim())) {
    return "Last name can only contain letters, spaces, hyphens, and apostrophes";
  }
  return null;
};

export const validateContactEmail = (email: string): string | null => {
  if (!email || email.trim().length === 0) {
    return null; // Will be handled by validateEmailOrMobile
  }
  if (!EMAIL_REGEX.test(email.trim())) {
    return "Please enter a valid email address";
  }
  if (email.trim().length > 254) {
    return "Email address is too long";
  }
  return null;
};

export const validateContactPhone = (phone: string): string | null => {
  if (!phone || phone.trim().length === 0) {
    return null; // Will be handled by validateEmailOrMobile
  }
  const cleanPhone = phone.replace(/[\s\-\(\)\.]/g, '');
  if (cleanPhone.length < 10) {
    return "Phone number must be at least 10 digits";
  }
  if (cleanPhone.length > 15) {
    return "Phone number must be less than 15 digits";
  }
  if (!/^[\+]?[0-9]+$/.test(cleanPhone)) {
    return "Please enter a valid phone number";
  }
  return null;
};

export const validateEmailOrMobile = (email: string, phone: string): string | null => {
  const hasEmail = email && email.trim().length > 0;
  const hasPhone = phone && phone.trim().length > 0;
  
  if (!hasEmail && !hasPhone) {
    return "Either email or mobile number is required";
  }
  
  return null;
};

export const validateContactMessage = (message: string): string | null => {
  if (!message || message.trim().length === 0) {
    return "Message is required";
  }
  if (message.trim().length < 10) {
    return "Message must be at least 10 characters";
  }
  if (message.trim().length > 1000) {
    return "Message must be less than 1000 characters";
  }
  return null;
};

// Complete contact form validation with enhanced features
export const validateContactForm = (formData: ContactFormData): ValidationResult => {
  const errors: ValidationErrors = {};
  let isValid = true;

  // Validate first name
  const firstNameError = validateFirstName(formData.firstName);
  if (firstNameError) {
    errors.firstName = firstNameError;
    isValid = false;
  }

  // Validate last name (optional)
  const lastNameError = validateLastName(formData.lastName);
  if (lastNameError) {
    errors.lastName = lastNameError;
    isValid = false;
  }

  // Validate email OR mobile requirement
  const emailOrMobileError = validateEmailOrMobile(formData.email, formData.phone);
  if (emailOrMobileError) {
    errors.emailOrMobile = emailOrMobileError;
    isValid = false;
  }

  // Validate email format if provided
  if (formData.email && formData.email.trim().length > 0) {
    const emailError = validateContactEmail(formData.email);
    if (emailError) {
      errors.email = emailError;
      isValid = false;
    }
  }

  // Validate phone format if provided
  if (formData.phone && formData.phone.trim().length > 0) {
    const phoneError = validateContactPhone(formData.phone);
    if (phoneError) {
      errors.phone = phoneError;
      isValid = false;
    }
  }

  // Validate message
  const messageError = validateContactMessage(formData.message);
  if (messageError) {
    errors.message = messageError;
    isValid = false;
  }

  return { isValid, errors };
};

// Sanitize HTML content (basic sanitization)
export const sanitizeHtml = (html: string): string => {
  // Remove script tags and their content
  let sanitized = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  
  // Remove dangerous attributes
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
  sanitized = sanitized.replace(/\s*javascript\s*:/gi, '');
  
  // Remove form elements for security
  sanitized = sanitized.replace(/<\s*\/?(?:form|input|textarea|select|option)\b[^>]*>/gi, '');
  
  return sanitized;
};
